import React, { forwardRef } from "react";
import classNames from "classnames";
import { Tooltip } from "../tooltip";
import { StyledProps } from "../_type";
import { useConfig } from "../_util/config-context";
import { forwardRefWithStatics } from "../_util/forward-ref-with-statics";
import { teaIconsMap } from "./tea-icons-map";

const colorClassArray = ["success", "warning", "pending", "error", "brand"];

const getColorClass = (color: string) => {
  return colorClassArray.includes(color);
};

export interface IconProps
  extends React.HTMLAttributes<HTMLElement>,
    StyledProps {
  /** 图标类型 */
  type: string;

  /**
   * 默认为 16x16 图标
   *
   * 状态图标支持配置为 32x32，包括：`success`、`infowaiting`、`infoblue` 和 `error`
   *
   * @default "default"
   */
  size?: "default" | "s" | "l" | number;

  /**
   * 是否渲染为超链接
   * @default false
   */
  link?: boolean;

  /**
   * 在图标上添加 Tooltip 的快捷方式，详见 [Tooltip](/component/tooltip) 组件
   */
  tooltip?: React.ReactNode;

  /**
   * 图标颜色，默认
   */
  color?: string;
}

export const Icon = forwardRefWithStatics(function Icon(
  props: IconProps,
  ref: React.Ref<HTMLElement>
) {
  const { classPrefix } = useConfig();
  const {
    type = "",
    size,
    className,
    tooltip,
    link,
    color,
    ...htmlProps
  } = props;

  const label = type.replace("-blue", "").replace("-hover", "");

  // 查看 type 是否在 @tencent/tea-icons-react 中是否存在映射
  const IconComponent = teaIconsMap[type];

  const iconClassName = classNames(
    className,
    {
      [`${classPrefix}-icon`]: !IconComponent,
      [`${classPrefix}-icon-${type}`]: !IconComponent,
      [`${classPrefix}-util-icon-${color}`]: getColorClass(color),
    },
    {
      "size-s": size === "s",
      "size-l": size === "l",
    }
  );

  let icon;

  // 没有映射，走老版本 icon
  if (!IconComponent) {
    const Parent = link ? "a" : "i";

    icon = (
      <Parent
        ref={ref as any}
        className={iconClassName}
        role="img"
        aria-label={label}
        {...htmlProps}
      />
    );
  } else {
    const newProps = {
      ref: ref as any,
      className: iconClassName,
      role: "img",
      "aria-label": label,
      ...htmlProps,
    };

    // size 是数字
    if (typeof size === "number") {
      Object.assign(newProps, { size });
    } else if (size === "l") {
      Object.assign(newProps, { size: 32 });
    }

    if (color) {
      Object.assign(newProps, { color });
    }

    // 找到映射分支
    icon = link ? (
      <a
        ref={ref as any}
        className={iconClassName}
        role="img"
        aria-label={label}
        {...htmlProps}
      >
        {React.cloneElement(IconComponent, newProps)}
      </a>
    ) : (
      React.cloneElement(IconComponent, newProps)
    );
  }

  if (tooltip) {
    return <Tooltip title={tooltip}>{icon}</Tooltip>;
  }

  return icon;
},
{});

Icon.displayName = "Icon";
